import { Component, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../shared/components/asset-header/asset-header.component';
import { HeaderComponent } from '../../shared/components/header/header.component';

export interface ModelTokenBreakdown {
  input: number;
  output: number;
  total: number;
}

export interface ModelUsageCard {
  modelId: string;
  name: string;
  problemStatement1: ModelTokenBreakdown;
  problemStatement2: ModelTokenBreakdown;
}

@Component({
  selector: 'app-models',
  standalone: true,
  imports: [HeaderComponent, AssetHeaderComponent],
  templateUrl: './models.component.html',
  styleUrl: './models.component.scss',
})
export class ModelsComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private copyClearTimer?: ReturnType<typeof setTimeout>;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Models' },
    ],
    title: 'Models',
    description:
      'Browse, run, and reuse AI models built to address real-world challenges across sectors.',
  };

  modelCards: ModelUsageCard[] = [
    {
      modelId: 'bedrock/converse/mistral.ministral-3-3b-instruct',
      name: 'Ministral 3B',
      problemStatement1: { input: 24_000_000, output: 1_800_000, total: 25_800_000 },
      problemStatement2: { input: 30_000_000, output: 3_000_000, total: 33_000_000 },
    },
    {
      modelId: 'bedrock/converse/mistral.ministral-3-8b-instruct',
      name: 'Ministral 8B',
      problemStatement1: { input: 24_000_000, output: 1_500_000, total: 25_500_000 },
      problemStatement2: { input: 30_000_000, output: 2_100_000, total: 32_100_000 },
    },
    {
      modelId: 'bedrock/converse/nvidia.nemotron-nano-3-30b',
      name: 'Nemotron Nano 30B',
      problemStatement1: { input: 24_000_000, output: 1_500_000, total: 25_500_000 },
      problemStatement2: { input: 30_000_000, output: 2_100_000, total: 32_100_000 },
    },
    {
      modelId: 'bedrock/converse/google.gemma-3-12b-it',
      name: 'Gemma 3 12B',
      problemStatement1: { input: 24_000_000, output: 1_500_000, total: 25_500_000 },
      problemStatement2: { input: 30_000_000, output: 2_100_000, total: 32_100_000 },
    },
    {
      modelId: 'bedrock/converse/google.gemma-3-4b-it',
      name: 'Gemma 3 4B',
      problemStatement1: { input: 24_000_000, output: 1_800_000, total: 25_800_000 },
      problemStatement2: { input: 30_000_000, output: 3_000_000, total: 33_000_000 },
    },
  ];

  formatIn(value: number): string {
    return new Intl.NumberFormat('en-IN').format(value);
  }

  copiedModelName: string | null = null;

  ngOnDestroy(): void {
    if (this.copyClearTimer) {
      clearTimeout(this.copyClearTimer);
    }
  }

  copyModelName(modelName: string): void {
    navigator.clipboard.writeText(modelName).then(
      () => {
        this.copiedModelName = modelName;
        if (this.copyClearTimer) {
          clearTimeout(this.copyClearTimer);
        }
        this.copyClearTimer = setTimeout(() => {
          this.copiedModelName = null;
          this.cdr.markForCheck();
        }, 2000);
        this.cdr.markForCheck();
      },
      err => {
        console.error('Failed to copy model name:', err);
      }
    );
  }
}
